import React, { useMemo, useState } from 'react'
import { PieChart, type PieChartSlice } from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './PieChartShowcase.css'

const SAMPLE_SLICES: PieChartSlice[] = [
  { value: 32, color: 'var(--ds-blue-500)', label: 'Hygiene' },
  { value: 24, color: 'var(--ds-blue-300)', label: 'Restorative' },
  { value: 18, color: 'var(--ds-blue-700)', label: 'Ortho' },
  { value: 14, color: 'var(--ds-blue-600)', label: 'Surgery' },
  { value: 12, color: 'var(--ds-blue-400)', label: 'Other' },
]

const sliceTotal = SAMPLE_SLICES.reduce((sum, slice) => sum + slice.value, 0)

const PieChartShowcase: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false)
  const [size, setSize] = useState(240)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'pie-chart-size',
            label: `Size (${size}px)`,
            control: (
              <input
                id="pie-chart-size"
                className="ui-field-control"
                type="range"
                min={120}
                max={320}
                step={8}
                value={size}
                onChange={(event) => setSize(Number(event.target.value))}
              />
            ),
          },
          {
            id: 'pie-chart-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="pie-chart-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, size],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Pie chart"
        description="Full circular segments in the design-system blue scale — data-driven slices for reusable charting."
        stageClassName="ui-preview__stage--wide pie-chart-showcase__stage-wrap"
      >
        <div
          className={`pie-chart-showcase__sheet${
            darkMode ? ' pie-chart-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Sample production mix · {SAMPLE_SLICES.length} categories
            </p>
            <div className="pie-chart-showcase__live-row pie-chart-showcase__live-row--sample">
              <PieChart
                size={size}
                slices={SAMPLE_SLICES}
                aria-label="Sample production mix"
              />
              <ul className="pie-chart-showcase__legend" aria-label="Legend">
                {SAMPLE_SLICES.map((slice) => {
                  const percent = Math.round((slice.value / sliceTotal) * 100)
                  return (
                    <li
                      key={slice.label}
                      className="pie-chart-showcase__legend-item"
                    >
                      <span
                        className="pie-chart-showcase__legend-swatch"
                        style={{ background: slice.color }}
                        aria-hidden="true"
                      />
                      <span className="pie-chart-showcase__legend-label">
                        {slice.label}
                      </span>
                      <span
                        className="pie-chart-showcase__legend-leader"
                        aria-hidden="true"
                      />
                      <span className="pie-chart-showcase__legend-value">
                        {percent}%
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          </section>

          <section className="pie-chart-showcase__matrix">
            <h4 className="pie-chart-showcase__section-title">Default</h4>
            <div
              className={`pie-chart-showcase__row${
                darkMode ? ' pie-chart-showcase__row--dark' : ''
              } pie-chart-showcase__row--selected`}
            >
              <span className="pie-chart-showcase__row-label">
                Figma default
              </span>
              <div className="pie-chart-showcase__row-preview">
                <PieChart />
              </div>
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Pie chart"
        sections={propertySections}
      />
    </>
  )
}

export default PieChartShowcase
